/**
 * Portal Settings Page — Profile + Organization management
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { PortalMembersPage } from './PortalMembersPage';
import type { Client } from '../../../shared/types/auth';
import './portal-responsive.css';

type SettingsSection = 'profile' | 'members' | 'organization' | 'billing';

const SECTION_ICONS: Record<SettingsSection, JSX.Element> = {
  profile: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  members: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  organization: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a4 4 0 0 0-8 0v2" />
    </svg>
  ),
  billing: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
};

export function PortalSettingsPage() {
  const { user, refreshUser, clientMemberships, isAdmin } = useAuth();
  const { client: layoutClient } = useOutletContext<{ client: Client | null }>();

  const myRole = clientMemberships[0]?.role;
  const isClientAdmin = isAdmin || myRole === 'client_admin';
  const isManager = isAdmin || (myRole && ({ viewer: 0, commenter: 1, people_manager: 2, client_admin: 3 }[myRole] ?? 0) >= 2);

  const [section, setSection] = useState<SettingsSection>('profile');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const navItems: { key: SettingsSection; label: string; show: boolean }[] = [
    { key: 'profile', label: 'Profile', show: true },
    { key: 'members', label: 'Members', show: !!isManager },
    { key: 'organization', label: 'Organization', show: isClientAdmin },
    { key: 'billing', label: 'Plan & Billing', show: isClientAdmin },
  ];

  const visibleItems = navItems.filter((i) => i.show);

  return (
    <div className="portal-page-content" style={{ maxWidth: 'none' }}>
      <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#f1f5f9', margin: 0, marginBottom: isMobile ? 16 : 24 }}>
        Settings
      </h1>

      {isMobile ? (
        <>
          {/* Mobile: horizontal tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
            {visibleItems.map((item) => (
              <TabBtn key={item.key} active={section === item.key} onClick={() => setSection(item.key)} label={item.label} isMobile />
            ))}
          </div>
          <div>
            {section === 'profile' && <ProfileSection user={user} refreshUser={refreshUser} isMobile />}
            {section === 'members' && isManager && <PortalMembersPage />}
            {section === 'organization' && isClientAdmin && layoutClient && (
              <OrganizationSection client={layoutClient} clientId={clientMemberships[0]?.clientId} isMobile />
            )}
            {section === 'billing' && isClientAdmin && layoutClient && (
              <BillingSection client={layoutClient} clientId={clientMemberships[0]?.clientId} isMobile />
            )}
          </div>
        </>
      ) : (
        /* Desktop: left sidebar + right content */
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Sidebar nav */}
          <nav style={{
            width: 220, flexShrink: 0, background: '#1a1a1a', border: '1px solid #333',
            borderRadius: 12, padding: 8, position: 'sticky', top: 80,
          }}>
            {visibleItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setSection(item.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '10px 14px', border: 'none', borderRadius: 8,
                  background: section === item.key ? '#1e293b' : 'transparent',
                  color: section === item.key ? '#f1f5f9' : '#94a3b8',
                  fontSize: 14, fontWeight: 500, cursor: 'pointer',
                  textAlign: 'left', transition: 'background 0.1s, color 0.1s',
                  borderLeft: section === item.key ? '3px solid #4F7CFF' : '3px solid transparent',
                }}
              >
                {SECTION_ICONS[item.key]}
                {item.label}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0, maxWidth: 640 }}>
            {section === 'profile' && <ProfileSection user={user} refreshUser={refreshUser} isMobile={false} />}
            {section === 'members' && isManager && <PortalMembersPage />}
            {section === 'organization' && isClientAdmin && layoutClient && (
              <OrganizationSection client={layoutClient} clientId={clientMemberships[0]?.clientId} isMobile={false} />
            )}
            {section === 'billing' && isClientAdmin && layoutClient && (
              <BillingSection client={layoutClient} clientId={clientMemberships[0]?.clientId} isMobile={false} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Profile Section ───────────────────────────────────────────────────────

function ProfileSection({ user, refreshUser, isMobile }: { user: any; refreshUser: () => Promise<void>; isMobile: boolean }) {
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ text: string; error?: boolean } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const photoUrl = user?.profilePhotoPath || null;
  const initials = (user?.name || user?.email || '?')
    .split(/[\s@]/).filter(Boolean).slice(0, 2).map((s: string) => s[0].toUpperCase()).join('');

  const handleSave = async () => {
    setSaving(true); setMessage('');
    try {
      await api.portal.updateProfile({ name });
      await refreshUser();
      setMessage('Profile updated');
    } catch (err: any) { setMessage(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setMessage('');
    try {
      await api.portal.uploadProfilePhoto(file);
      await refreshUser();
      setMessage('Photo updated');
    } catch (err: any) { setMessage(err.message || 'Failed to upload photo'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleRemovePhoto = async () => {
    setUploading(true); setMessage('');
    try {
      await api.portal.removeProfilePhoto();
      await refreshUser();
      setMessage('Photo removed');
    } catch (err: any) { setMessage(err.message || 'Failed to remove photo'); }
    finally { setUploading(false); }
  };

  const handleChangePassword = async () => {
    setPasswordMessage(null);
    if (!currentPassword || !newPassword) { setPasswordMessage({ text: 'All fields are required', error: true }); return; }
    if (newPassword !== confirmPassword) { setPasswordMessage({ text: 'New passwords do not match', error: true }); return; }
    if (newPassword.length < 8) { setPasswordMessage({ text: 'New password must be at least 8 characters', error: true }); return; }
    setChangingPassword(true);
    try {
      await api.auth.changePassword(currentPassword, newPassword);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setPasswordMessage({ text: 'Password changed successfully' });
    } catch (err: any) { setPasswordMessage({ text: err.message || 'Failed to change password', error: true }); }
    finally { setChangingPassword(false); }
  };

  return (
    <div style={{ ...styles.card, ...(isMobile ? { padding: 16 } : {}) }}>
      <h2 style={styles.sectionTitle}>Profile Photo</h2>
      <div style={{ ...styles.photoRow, ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'center', gap: 16 } : {}) }}>
        <div
          style={{ ...styles.avatar, ...(photoUrl ? { backgroundImage: `url(${photoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}) }}
          onClick={() => fileInputRef.current?.click()}
          title="Click to change photo"
        >
          {!photoUrl && <span style={styles.avatarInitials}>{initials}</span>}
        </div>
        <div style={{ ...styles.photoActions, ...(isMobile ? { flexDirection: 'row' as const, width: '100%', justifyContent: 'center' } : {}) }}>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ ...styles.btnPrimary, ...(isMobile ? { minHeight: 44, fontSize: 14, padding: '10px 20px' } : {}) }}>
            {uploading ? 'Uploading...' : 'Change Photo'}
          </button>
          {photoUrl && (
            <button onClick={handleRemovePhoto} disabled={uploading} style={{ ...styles.btnDanger, ...(isMobile ? { minHeight: 44, fontSize: 14, padding: '10px 20px' } : {}) }}>
              Remove
            </button>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />
      </div>

      <h2 style={{ ...styles.sectionTitle, marginTop: isMobile ? 24 : 32 }}>Account</h2>
      <label style={styles.label}>Name</label>
      <input type="text" value={name} onChange={e => setName(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="Your name" />
      <label style={styles.label}>Email</label>
      <div style={{ ...styles.readOnly, ...(isMobile ? { fontSize: 16, minHeight: 44, display: 'flex', alignItems: 'center' } : {}) }}>{user?.email}</div>
      <div style={{ ...styles.actions, ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'stretch' } : {}) }}>
        <button onClick={handleSave} disabled={saving} style={{ ...styles.btnPrimary, ...(isMobile ? { minHeight: 44, fontSize: 14, padding: '10px 20px' } : {}) }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        {message && <span style={{ ...styles.message, ...(isMobile ? { textAlign: 'center' as const } : {}) }}>{message}</span>}
      </div>

      {user?.role !== 'admin' && (
        <>
          <h2 style={{ ...styles.sectionTitle, marginTop: isMobile ? 24 : 32 }}>Change Password</h2>
          <label style={styles.label}>Current Password</label>
          <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="Enter current password" />
          <label style={styles.label}>New Password</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="Enter new password" />
          <label style={styles.label}>Confirm New Password</label>
          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="Confirm new password" />
          <div style={{ ...styles.actions, ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'stretch' } : {}) }}>
            <button onClick={handleChangePassword} disabled={changingPassword} style={{ ...styles.btnPrimary, ...(isMobile ? { minHeight: 44, fontSize: 14, padding: '10px 20px' } : {}) }}>
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
            {passwordMessage && (
              <span style={{ ...styles.message, color: passwordMessage.error ? '#ef4444' : '#22c55e', ...(isMobile ? { textAlign: 'center' as const } : {}) }}>
                {passwordMessage.text}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Organization Section ──────────────────────────────────────────────────

function OrganizationSection({ client, clientId, isMobile }: { client: Client; clientId: string; isMobile: boolean }) {
  // Org details
  const [contactEmail, setContactEmail] = useState(client.contactEmail || '');
  const [contactName, setContactName] = useState(client.contactName || '');
  const [description, setDescription] = useState(client.notes || '');
  const [logoPath, setLogoPath] = useState(client.logoPath || '');
  const [logoUploading, setLogoUploading] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgMessage, setOrgMessage] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Enrollment
  const [enrollment, setEnrollment] = useState<{ enrollmentCode: string | null; enrollmentEnabled: boolean; autoApproveMode: string; autoApproveDomains: string | null } | null>(null);
  const [enrollSaving, setEnrollSaving] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const loadEnrollment = useCallback(async () => {
    try {
      const data = await api.portal.getEnrollment(clientId);
      setEnrollment(data);
    } catch { /* non-critical */ }
  }, [clientId]);

  useEffect(() => { loadEnrollment(); }, [loadEnrollment]);

  const handleSaveOrg = async () => {
    setOrgSaving(true); setOrgMessage('');
    try {
      await api.portal.updateClient(clientId, { contactEmail, contactName, notes: description });
      setOrgMessage('Organization updated');
    } catch (err: any) { setOrgMessage(err.message || 'Failed to save'); }
    finally { setOrgSaving(false); }
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const result = await api.portal.uploadLogo(clientId, reader.result as string);
          setLogoPath(result.logoPath);
        } catch (err: any) { setOrgMessage(err.message || 'Failed to upload logo'); }
        finally { setLogoUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch { setLogoUploading(false); }
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleRemoveLogo = async () => {
    setLogoUploading(true);
    try {
      await api.portal.removeLogo(clientId);
      setLogoPath('');
    } catch (err: any) { setOrgMessage(err.message || 'Failed to remove logo'); }
    finally { setLogoUploading(false); }
  };

  const toggleEnrollment = async (enabled: boolean) => {
    setEnrollSaving(true);
    try {
      const updated = await api.portal.updateEnrollment(clientId, { enrollmentEnabled: enabled });
      setEnrollment(updated);
    } catch (err) { console.error('[Settings] Toggle enrollment error:', err); }
    finally { setEnrollSaving(false); }
  };

  const updateAutoApprove = async (mode: string) => {
    setEnrollSaving(true);
    try {
      const updated = await api.portal.updateEnrollment(clientId, { autoApproveMode: mode });
      setEnrollment(updated);
    } catch (err) { console.error('[Settings] Update auto-approve error:', err); }
    finally { setEnrollSaving(false); }
  };

  const updateDomains = async (domains: string) => {
    setEnrollSaving(true);
    try {
      const updated = await api.portal.updateEnrollment(clientId, { autoApproveDomains: domains });
      setEnrollment(updated);
    } catch (err) { console.error('[Settings] Update domains error:', err); }
    finally { setEnrollSaving(false); }
  };

  const regenerateCode = async () => {
    setEnrollSaving(true);
    try {
      const result = await api.portal.regenerateEnrollmentCode(clientId);
      setEnrollment((prev) => prev ? { ...prev, enrollmentCode: result.enrollmentCode } : prev);
    } catch (err) { console.error('[Settings] Regenerate code error:', err); }
    finally { setEnrollSaving(false); }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch { /* clipboard not available */ }
  };

  const handleShare = async () => {
    if (!enrollment?.enrollmentCode) return;
    const signupUrl = `${window.location.origin}/signup?code=${enrollment.enrollmentCode}`;
    const text = `Join our organization on CoreSkan! Use enrollment code: ${enrollment.enrollmentCode}\n\nSign up here: ${signupUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Join our organization', text }); } catch { /* cancelled */ }
    } else {
      copyToClipboard(text);
    }
  };

  return (
    <>
      {/* Company Profile */}
      <div style={{ ...styles.card, ...(isMobile ? { padding: 16 } : {}), marginBottom: 20 }}>
        <h2 style={styles.sectionTitle}>Company Profile</h2>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-start', gap: 20, marginBottom: 24, flexDirection: isMobile ? 'column' : 'row' }}>
          <div
            onClick={() => logoInputRef.current?.click()}
            style={{
              width: 100, height: 100, borderRadius: 12, border: '2px dashed #475569',
              background: logoPath ? 'transparent' : '#0d0d0d',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0, overflow: 'hidden',
              transition: 'border-color 0.15s',
            }}
            title="Click to upload logo"
          >
            {logoPath ? (
              <img src={logoPath} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <div style={{ textAlign: 'center', color: '#475569' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
                </svg>
                <div style={{ fontSize: 10, marginTop: 4 }}>Upload</div>
              </div>
            )}
          </div>
          <div style={{ flex: 1, ...(isMobile ? { width: '100%', textAlign: 'center' } : {}) }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: isMobile ? 'center' : 'flex-start' }}>
              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                style={{ ...styles.btnPrimary, ...(isMobile ? { minHeight: 44, flex: 1 } : {}) }}
              >
                {logoUploading ? 'Uploading...' : logoPath ? 'Change Logo' : 'Upload Logo'}
              </button>
              {logoPath && (
                <button onClick={handleRemoveLogo} disabled={logoUploading} style={{ ...styles.btnDanger, ...(isMobile ? { minHeight: 44 } : {}) }}>
                  Remove
                </button>
              )}
            </div>
            <div style={{ color: '#475569', fontSize: 11, marginTop: 8 }}>
              Recommended: square image, at least 200x200px. PNG or JPG.
            </div>
          </div>
          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoSelect} />
        </div>

        {/* Org name (read-only) */}
        <label style={styles.label}>Organization Name</label>
        <div style={styles.readOnly}>{client.name}</div>

        {/* Company description */}
        <label style={styles.label}>Company Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          style={{ ...styles.input, fontSize: 14, minHeight: 80, resize: 'vertical' as const }}
          placeholder="Tell us about your company — what you do, your industry, and your focus areas."
          rows={3}
        />

        <div style={{ borderTop: '1px solid #333', margin: '24px 0 8px' }} />

        {/* Contact info */}
        <label style={styles.label}>Contact Name</label>
        <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="Primary contact name" />

        <label style={styles.label}>Contact Email</label>
        <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="contact@company.com" />

        <div style={{ ...styles.actions, ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'stretch' } : {}) }}>
          <button onClick={handleSaveOrg} disabled={orgSaving} style={{ ...styles.btnPrimary, ...(isMobile ? { minHeight: 44, fontSize: 14, padding: '10px 20px' } : {}) }}>
            {orgSaving ? 'Saving...' : 'Save Changes'}
          </button>
          {orgMessage && <span style={{ ...styles.message, ...(isMobile ? { textAlign: 'center' as const } : {}) }}>{orgMessage}</span>}
        </div>

        <div style={{ color: '#475569', fontSize: 12, marginTop: 16 }}>
          To change your organization name, contact CoreSkan support.
        </div>
      </div>

      {/* Self-Enrollment */}
      {enrollment && (
        <div style={{ ...styles.card, ...(isMobile ? { padding: 16 } : {}) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Self-Enrollment</h2>
            <button
              onClick={() => toggleEnrollment(!enrollment.enrollmentEnabled)}
              disabled={enrollSaving}
              style={{
                background: enrollment.enrollmentEnabled ? '#166534' : '#374151',
                color: enrollment.enrollmentEnabled ? '#86efac' : '#9ca3af',
                border: 'none', borderRadius: 20, padding: '6px 16px', fontSize: 12, fontWeight: 600,
                cursor: enrollSaving ? 'not-allowed' : 'pointer', opacity: enrollSaving ? 0.6 : 1,
                ...(isMobile ? { minHeight: 40 } : {}),
              }}
            >
              {enrollment.enrollmentEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {enrollment.enrollmentEnabled ? (
            <>
              {enrollment.enrollmentCode && (
                <>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Enrollment Code</div>
                  <div style={{
                    display: 'flex', alignItems: isMobile ? 'stretch' : 'center', gap: 10,
                    flexDirection: isMobile ? 'column' : 'row', marginBottom: 16,
                  }}>
                    <div style={{
                      background: '#0d0d0d', border: '1px solid #333333', borderRadius: 8,
                      padding: isMobile ? '14px 16px' : '10px 16px', fontFamily: 'monospace',
                      fontSize: isMobile ? 20 : 16, color: '#6B95FF', letterSpacing: '0.15em',
                      fontWeight: 600, textAlign: isMobile ? 'center' : 'left',
                      flex: isMobile ? undefined : 1, userSelect: 'all',
                    }}>
                      {enrollment.enrollmentCode}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => copyToClipboard(enrollment.enrollmentCode!)} style={{ ...styles.btnSmall, background: codeCopied ? '#166534' : '#4F7CFF', color: codeCopied ? '#86efac' : '#fff', ...(isMobile ? { minHeight: 44, flex: 1 } : {}) }}>
                        {codeCopied ? 'Copied!' : 'Copy'}
                      </button>
                      <button onClick={handleShare} style={{ ...styles.btnSmallOutline, ...(isMobile ? { minHeight: 44, flex: 1 } : {}) }}>
                        Share
                      </button>
                      <button onClick={regenerateCode} disabled={enrollSaving} style={{ ...styles.btnSmallOutline, ...(isMobile ? { minHeight: 44, flex: 1 } : {}) }} title="Generate a new code (old code stops working)">
                        Regenerate
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>When someone signs up with this code</div>
              <select
                value={enrollment.autoApproveMode}
                onChange={(e) => updateAutoApprove(e.target.value)}
                disabled={enrollSaving}
                style={{ ...styles.input, marginBottom: 12, ...(isMobile ? { fontSize: 16, minHeight: 44 } : {}) }}
              >
                <option value="enrollment_code">Auto-approve (valid code is enough)</option>
                <option value="email_domain">Auto-approve only matching email domains</option>
                <option value="both">Require valid code AND matching email domain</option>
                <option value="manual">Require manual approval for all</option>
              </select>

              {(enrollment.autoApproveMode === 'email_domain' || enrollment.autoApproveMode === 'both') && (
                <>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Approved Email Domains</div>
                  <input
                    type="text"
                    value={enrollment.autoApproveDomains || ''}
                    onChange={(e) => setEnrollment({ ...enrollment, autoApproveDomains: e.target.value })}
                    onBlur={(e) => updateDomains(e.target.value)}
                    placeholder="company.com, contractor.com"
                    style={{ ...styles.input, marginBottom: 4, ...(isMobile ? { fontSize: 16 } : {}) }}
                  />
                  <div style={{ color: '#475569', fontSize: 11, marginBottom: 12 }}>Comma-separated list of email domains</div>
                </>
              )}

              <div style={{ color: '#64748b', fontSize: 12 }}>
                Share this code with team members so they can sign up and join your organization.
              </div>
            </>
          ) : (
            <div style={{ color: '#64748b', fontSize: 13 }}>
              Enable self-enrollment to allow team members to sign up with an enrollment code and automatically join your organization.
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─── Billing Section ───────────────────────────────────────────────────────

function BillingSection({ client, clientId, isMobile }: { client: Client; clientId: string; isMobile: boolean }) {
  const [billingEmail, setBillingEmail] = useState(client.billingEmail || '');
  const [billingStreet, setBillingStreet] = useState(client.billingStreet || '');
  const [billingCity, setBillingCity] = useState(client.billingCity || '');
  const [billingState, setBillingState] = useState(client.billingState || '');
  const [billingZip, setBillingZip] = useState(client.billingZip || '');
  const [billingCountry, setBillingCountry] = useState(client.billingCountry || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleSave = async () => {
    setSaving(true); setMessage('');
    try {
      await api.portal.updateClient(clientId, { billingEmail, billingStreet, billingCity, billingState, billingZip, billingCountry });
      setMessage('Billing updated');
    } catch (err: any) { setMessage(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <>
      {/* Current Plan */}
      <div style={{ ...styles.card, ...(isMobile ? { padding: 16 } : {}), marginBottom: 20 }}>
        <h2 style={styles.sectionTitle}>Current Plan</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: '#0d0d0d', border: '1px solid #333', borderRadius: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>{client.planName || 'Standard'}</div>
            {client.memberLimit && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Up to {client.memberLimit} members</div>
            )}
          </div>
          <div style={{ background: '#166534', color: '#86efac', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
            Active
          </div>
        </div>
        <div style={{ color: '#475569', fontSize: 12, marginTop: 12 }}>
          To change your plan, contact CoreSkan support.
        </div>
      </div>

      {/* Billing Details */}
      <div style={{ ...styles.card, ...(isMobile ? { padding: 16 } : {}) }}>
        <h2 style={styles.sectionTitle}>Billing Details</h2>

        <label style={styles.label}>Billing Email</label>
        <input type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="billing@company.com" />

        <label style={{ ...styles.label, marginTop: 20 }}>Billing Address</label>
        <input type="text" value={billingStreet} onChange={e => setBillingStreet(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44, marginBottom: 8 }} placeholder="Street address" />
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <input type="text" value={billingCity} onChange={e => setBillingCity(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="City" />
          <input type="text" value={billingState} onChange={e => setBillingState(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="State / Province" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
          <input type="text" value={billingZip} onChange={e => setBillingZip(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="ZIP / Postal code" />
          <input type="text" value={billingCountry} onChange={e => setBillingCountry(e.target.value)} style={{ ...styles.input, fontSize: 16, minHeight: 44 }} placeholder="Country" />
        </div>

        <div style={{ ...styles.actions, ...(isMobile ? { flexDirection: 'column' as const, alignItems: 'stretch' } : {}) }}>
          <button onClick={handleSave} disabled={saving} style={{ ...styles.btnPrimary, ...(isMobile ? { minHeight: 44, fontSize: 14, padding: '10px 20px' } : {}) }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {message && <span style={{ ...styles.message, ...(isMobile ? { textAlign: 'center' as const } : {}) }}>{message}</span>}
        </div>
      </div>
    </>
  );
}

// ─── Tab Button ────────────────────────────────────────────────────────────

function TabBtn({ active, onClick, label, isMobile }: { active: boolean; onClick: () => void; label: string; isMobile: boolean }) {
  return (
    <button onClick={onClick} style={{
      background: active ? '#1e293b' : 'transparent',
      color: active ? '#f1f5f9' : '#64748b',
      border: '1px solid', borderColor: active ? '#334155' : 'transparent',
      borderRadius: 6, padding: isMobile ? '10px 18px' : '6px 14px',
      fontSize: isMobile ? 14 : 13, fontWeight: 500, cursor: 'pointer',
      ...(isMobile ? { minHeight: 44 } : {}),
    }}>
      {label}
    </button>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: 12,
    padding: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#94a3b8',
    margin: '0 0 16px',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  photoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    background: '#334155',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    border: '2px solid #475569',
    transition: 'border-color 0.15s',
  },
  avatarInitials: {
    fontSize: 28,
    fontWeight: 700,
    color: '#94a3b8',
  },
  photoActions: {
    display: 'flex',
    gap: 8,
    flexDirection: 'column' as const,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: '#94a3b8',
    marginBottom: 6,
    marginTop: 16,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    background: '#0d0d0d',
    border: '1px solid #333',
    borderRadius: 8,
    color: '#f1f5f9',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  readOnly: {
    padding: '10px 12px',
    background: '#0d0d0d',
    border: '1px solid #282828',
    borderRadius: 8,
    color: '#64748b',
    fontSize: 14,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 24,
  },
  btnPrimary: {
    padding: '8px 16px',
    background: '#3b82f6',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnDanger: {
    padding: '8px 16px',
    background: 'none',
    border: '1px solid #475569',
    borderRadius: 8,
    color: '#94a3b8',
    fontSize: 13,
    cursor: 'pointer',
  },
  btnSmall: {
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  btnSmallOutline: {
    background: 'transparent',
    color: '#94a3b8',
    border: '1px solid #475569',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  message: {
    fontSize: 13,
    color: '#22c55e',
  },
};
